import type { LoanComp } from "../../types/deal";

/**
 * Values held by the loan comp form / extracted-comps grid. Starts from the GET
 * shape, but inputs hand back strings for numbers, `country` / `sector` may be
 * any `{ id }` option object (or null when cleared), and spreadsheet rows can
 * carry arbitrary extra columns and `Date` objects.
 */
export type LoanCompFormValues = {
  [K in keyof LoanComp]?: unknown;
} & Record<string, unknown>;

/**
 * Body accepted by POST /loan-comps and PUT /loan-comp/{id}.
 *
 * The backend create does `LoanComp(**payload)` (unknown keys -> TypeError -> 500)
 * and the update `setattr`s every key that is a model attribute, skipping only
 * `id`, `country` and `sector`. So the relationship objects must be sent as
 * `country_id` / `sector_id`, and only real columns may be sent.
 *
 * Import metadata (`source_row`, `record_id`) and the manual-validation columns are
 * deliberately not part of the form payload: they are not user-editable here and
 * omitting them leaves the stored values untouched on update.
 */
export interface LoanCompPayload {
  loan_type: string | null;
  date: string | null;
  sponsor: string | null;
  project_name: string | null;
  country_id: number | null;
  city: string | null;
  sector_id: number | null;
  ltc_ltv: number | null;
  margin: number | null;
  loan_amount_local: number | null;
  est_local_irr: number | null;
  lender_type: string | null;
  comment: string | null;
  irr: number | null;
}

const TEXT_FIELDS = [
  "loan_type",
  "sponsor",
  "project_name",
  "city",
  "lender_type",
  "comment",
] as const;

const NUMBER_FIELDS = [
  "ltc_ltv",
  "margin",
  "loan_amount_local",
  "est_local_irr",
  "irr",
] as const;

const pad = (n: number) => String(n).padStart(2, "0");

export function toNullableText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}

export function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/,/g, "").trim();
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Normalise to the `YYYY-MM-DD` string the backend `Date` column accepts. */
export function toNullableDate(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    // Local calendar date: spreadsheet dates are built as local midnight, so
    // toISOString() could shift them back a day.
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

function toPositiveId(value: unknown): number | null {
  const id = toNullableNumber(value);
  return id !== null && Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Resolve a FK id from the relationship object (`country` / `sector`) when the
 * form holds one — including `null` after the user cleared the select — and fall
 * back to the raw `*_id` otherwise. Unmatched placeholder objects (id 0) map to null.
 */
function resolveRelationId(
  values: LoanCompFormValues,
  relationKey: "country" | "sector",
  idKey: "country_id" | "sector_id",
): number | null {
  if (relationKey in values) {
    const relation = values[relationKey];
    if (relation === null || relation === undefined || relation === "") {
      return null;
    }
    if (typeof relation === "object" && "id" in relation) {
      return toPositiveId((relation as { id: unknown }).id);
    }
    return toPositiveId(relation);
  }
  return toPositiveId(values[idKey]);
}

/** Build a create/update body containing only persisted loan comp columns. */
export function buildLoanCompPayload(values: LoanCompFormValues): LoanCompPayload {
  const payload = {
    date: toNullableDate(values.date),
    country_id: resolveRelationId(values, "country", "country_id"),
    sector_id: resolveRelationId(values, "sector", "sector_id"),
  } as LoanCompPayload;

  for (const key of TEXT_FIELDS) {
    payload[key] = toNullableText(values[key]);
  }
  for (const key of NUMBER_FIELDS) {
    payload[key] = toNullableNumber(values[key]);
  }
  return payload;
}

/** True when every field the backend requires (`loan_type`, NOT NULL) is filled. */
export function hasRequiredLoanCompFields(values: LoanCompFormValues): boolean {
  return toNullableText(values.loan_type) !== null;
}
